// Client for the "Test Note" flow: re-run note generation for a patient against
// an admin-chosen CONVERSATION_PROMPT version, then diff the result against the
// note production actually produced.
//
// Backed by server/routes/testNote.js, which uses the summarize pipeline's
// non-production execution mode. Results land in the test/eval summarize table
// flagged `is_test`, and no production side-effects fire.

import { environment } from "../../config/environment";
import { customFetch } from "../../utils/Auth";
import { getTemplateDisplayTime } from "../Templates/timeUtils";

/** Every CONVERSATION_PROMPT version of `name`, newest first. */
export async function getConversationPromptVersions(name) {
  const trimmed = name?.trim();
  if (!trimmed) return { error: "No conversation prompt name" };

  try {
    const response = await customFetch(
      `${environment.apiUrl}/templates/get-all-templates`,
      { method: "GET" }
    );
    if (!response.ok) {
      return { error: "Failed to fetch template versions" };
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      return { error: "Unexpected template list response" };
    }

    const versions = data.filter(
      (template) =>
        template?.name === trimmed && template?.type === "CONVERSATION_PROMPT"
    );

    if (versions.length === 0) {
      return {
        error: `No CONVERSATION_PROMPT versions found for "${trimmed}"`,
      };
    }

    // The list API has no guaranteed ordering; sort newest first using the same
    // timestamp resolution the Templates page uses, so both views agree on
    // which version is the most recent.
    versions.sort(
      (a, b) => getTemplateDisplayTime(b) - getTemplateDisplayTime(a)
    );

    return { versions };
  } catch {
    return { error: "Failed to fetch template versions" };
  }
}

/**
 * Queues a test-note run for the patient's most recent summarize job.
 * Omitting the prompt name/version re-runs with the workflow default.
 */
export async function startTestNoteRun({
  patientId,
  convPromptName,
  convPromptVersion,
}) {
  if (!patientId) {
    return { success: false, error: "No patient ID" };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/test-note/run`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patientId,
          convPromptName: convPromptName || undefined,
          convPromptVersion: convPromptVersion || undefined,
        }),
      }
    );
    const result = await response.json();

    if (!response.ok || result.status !== "success") {
      return {
        success: false,
        error: result.error || "Failed to start test note run",
      };
    }

    return { success: true, run: result };
  } catch {
    return { success: false, error: "Failed to start test note run." };
  }
}

/**
 * Promotes a template version to default — the version the production pipeline
 * resolves when no explicit version is requested. Same endpoint the Templates
 * page uses, so the two views stay consistent.
 */
export async function setTemplateVersionAsDefault(name, version) {
  if (!name || !version) {
    return { success: false, error: "Template name and version are required" };
  }

  try {
    const response = await customFetch(
      `${environment.apiUrl}/templates/set-default`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, version }),
      }
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        error:
          data?.details || data?.error || "Failed to set template as default",
      };
    }

    return { success: true };
  } catch {
    return { success: false, error: "Failed to set template as default." };
  }
}

/**
 * One poll for a queued run's row.
 * @returns {Promise<{ready: boolean, job?: object} | {error: string}>}
 *   `ready: false` means the lambda has not written the row yet — keep waiting.
 */
export async function fetchTestNoteResult(testSummarizeJobName) {
  if (!testSummarizeJobName) return { error: "No test summarize job name" };

  try {
    const response = await customFetch(
      `${environment.apiUrl}/test-note/${encodeURIComponent(
        testSummarizeJobName
      )}`,
      { method: "GET" }
    );
    const data = await response.json();

    if (!response.ok) {
      return { error: data?.error || "Failed to fetch test note result" };
    }

    return data;
  } catch {
    return { error: "Failed to fetch test note result" };
  }
}
